import React, { useEffect, useRef, useState } from "react";
import { useContent } from "../../../contexts/contentContext/contentContext.js";
import TooltipPortal from "./TooltipPortal.js";
import useOnClickOutside from "hooks/useOnClickOutside.js";
import useMediaQueries from "hooks/useMediaQueries.js";

import "./Tooltip.scss";

/**
 *
 * @param {String} tooltipText - text to display in tooltip
 * @param {String} tooltipTextKey - text key to display in tooltip
 * @param {Number} margin - margin from side of an element to tooltip
 * @param {String} className - additional classname to tooltip span wrapper
 * @param {Boolean} clickable - if true, tooltip will be opened by click, closed by click or mouseleave
 * @returns - wrapped component with tooltip as portal
 */

const Tooltip = ({
  tooltipText = "",
  tooltipTextKey,
  margin = 10,
  className = "",
  delay = 500,
  clickable = false,
  externalControl,
  children,
  hardPositioned = false,
  hardPositionedPosition = false,
  mobileClickability = false,
  ...rest
}) => {
  const [tooltipState, setTooltipState] = useState({
    isVisible: false,
    position: "top",
  });

  const [tooltipCoords, setTooltipCoords] = useState({ top: 0, left: 0 });
  const [isTabletClicked, setIsTabletClicked] = useState(false);
  //set classes for this toolip
  //set position (top is default)
  const { start, end, top, bottom } = rest;
  const { getLanguage, getTextByKey, getDirection } = useContent();
  const { desktopCondition } = useMediaQueries();

  const position = useRef("top");

  // function to hanlde the tooltip position
  const setPosition = (positionString) => {
    position.current = positionString;
    setTooltipState((prev) => ({ ...prev, position: positionString }));
  };

  // function to handle the tooltip visibility
  // in case tooltip hidden set default position
  const handleVisibleTooltip = (isVisible) => {
    if (!isVisible) position.current = "top";
    setTooltipState((prev) => ({
      position: !isVisible ? "top" : prev.position,
      isVisible: isVisible,
    }));
  };

  //set the tooltip location on very mouse over
  const onMouseHoverSetLocation = (getBoundingClientRect) => {
    //set tooltip location according to its prop & language
    if (start) {
      getLanguage() === "he" ? setPosition("right") : setPosition("left");
    } else if (end) {
      getLanguage() === "he" ? setPosition("left") : setPosition("right");
    } else if (bottom) {
      setPosition("bottom");
    } else if (top) {
      setPosition("top");
    }
    // When tool tip is going out of the window force the tooltip to go to other side
    if (getBoundingClientRect && !hardPositioned) {
      const headerBottomPosition = document.querySelector(".content-header")
        ? document.querySelector(".content-header").getBoundingClientRect()
            .top +
          document.querySelector(".content-header").getBoundingClientRect()
            .height
        : null;
      const isUnderHeader = headerBottomPosition
        ? getBoundingClientRect.top - headerBottomPosition < 100 &&
          getBoundingClientRect.top - headerBottomPosition > 0
        : false;

      if (getBoundingClientRect.left < 200) {
        if (
          getBoundingClientRect.top >= 140 &&
          getBoundingClientRect.left > 200 &&
          !isUnderHeader
        ) {
          setPosition("top");
        } else if (
          getBoundingClientRect.top < 120 &&
          getBoundingClientRect.left > 200
        ) {
          setPosition("bottom");
        } else {
          if (!hardPositionedPosition) {
            setPosition("right");
          }
        }
      } else if (window.innerWidth - getBoundingClientRect.right < 200) {
        if (
          getBoundingClientRect.top >= 140 &&
          window.innerWidth - getBoundingClientRect.right > 200 &&
          !isUnderHeader
        ) {
          setPosition("top");
        } else if (
          getBoundingClientRect.top < 120 &&
          window.innerWidth - getBoundingClientRect.right > 200
        ) {
          setPosition("bottom");
        } else {
          setPosition("left");
        }
      } else if (getBoundingClientRect.top < 120) {
        setPosition("bottom");
      } else if (window.innerHeight - getBoundingClientRect.bottom < 120) {
        setPosition("top");
      } else if (isUnderHeader) {
        window.innerHeight - getBoundingClientRect.bottom > 120
          ? setPosition("bottom")
          : window.innerWidth - getBoundingClientRect.right > 200
          ? setPosition("right")
          : getBoundingClientRect.left > 200 && setPosition("left");
      }
      handleShow(getBoundingClientRect);
    }
  };

  const getParentSizes = (getBoundingClientRect) => {
    /**
     * @param {Number} margin - takes from props
     * @param {Number} containerSizeContsant - width of tooltip from css file cassName`jm-tooltip`
     * @returns {Object} - depence on position of tooltip return object with top and left
     */

    const containerSizeContsant = 250;

    if (position.current === "top") {
      return {
        top: -margin - containerSizeContsant,
        left: getBoundingClientRect.width / 2 - containerSizeContsant / 2,
      };
    }
    if (position.current === "bottom") {
      return {
        top: getBoundingClientRect.height + margin,
        left: getBoundingClientRect.width / 2 - containerSizeContsant / 2,
      };
    }
    if (position.current === "left") {
      return {
        top: getBoundingClientRect.height / 2 - containerSizeContsant / 2,
        left: -margin - containerSizeContsant,
      };
    }
    if (position.current === "right") {
      return {
        top: getBoundingClientRect.height / 2 - containerSizeContsant / 2,
        left: getBoundingClientRect.width + margin,
      };
    }
  };

  const handleShow = (getBoundingClientRect) => {
    closed.current = false;
    setTimeout(() => {
      if (!closed.current) {
        handleVisibleTooltip(true);
        const parentSizes = getParentSizes(getBoundingClientRect);
        setTooltipCoords({
          top: getBoundingClientRect.top + parentSizes.top + "px",
          left: getBoundingClientRect.left + parentSizes.left + "px",
        });
      }
    }, delay);
  };

  const externalControlRef = useRef();
  const closed = useRef(true);

  // handle hide tooltip on window scroll
  const hideOnScroll = () => {
    handleHide();
  };

  useEffect(() => {
    window.addEventListener("wheel", hideOnScroll);
    return () => {
      window.removeEventListener("wheel", hideOnScroll);
    };
  }, []);

  useEffect(() => {
    setTooltipState((prev) => ({ ...prev, isVisible: externalControl }));
  }, [externalControl]);

  const handleHide = () => {
    closed.current = true;
    handleVisibleTooltip(false);
  };

  const handleRef = useRef();

  useOnClickOutside(handleRef, handleHide);

  const handleClickTabletAndMobile = (el) => {
    if (!desktopCondition && mobileClickability) {
      const oldTabletState = isTabletClicked;
      setIsTabletClicked((prev) => !prev);
      if (!oldTabletState) {
        onMouseHoverSetLocation(el.currentTarget?.getBoundingClientRect());
      } else {
        handleHide();
      }
    }
  };

  const touchAndHoldRef = useRef();

  return (
    <>
      <span
        ref={externalControlRef}
        onMouseEnter={(el) => {
          onMouseHoverSetLocation(el.currentTarget?.getBoundingClientRect());
        }}
        onMouseLeave={() => {
          if (!clickable) {
            handleHide();
          }
        }}
        className="tooltip-controller-holder"
        onClick={(el) => {
          !desktopCondition && mobileClickability
            ? handleClickTabletAndMobile(el)
            : handleHide();
        }}
        onTouchStart={(el) => {
          el.preventDefault();
          touchAndHoldRef.current = true;
          setTimeout(() => {
            if (touchAndHoldRef.current) {
              onMouseHoverSetLocation(
                el.currentTarget?.getBoundingClientRect()
              );
            }
          }, 500);
        }}
        onTouchEnd={() => {
          touchAndHoldRef.current = false;
          handleHide();
        }}
      >
        {children}
      </span>
      {tooltipState.isVisible ? (
        <TooltipPortal>
          <div
            className={`jm-tooltip ${className} ${
              tooltipState.position
            } ${getDirection()}-tooltip`}
            style={tooltipCoords}
            ref={handleRef}
          >
            <span
              style={{
                cursor: clickable ? "pointer" : "default",
                pointerEvents: clickable ? "auto" : "none",
              }}
              onClick={handleHide}
              onMouseLeave={() => {
                if (clickable) {
                  handleHide();
                }
              }}
            >
              {tooltipText
                ? tooltipText
                : getTextByKey(tooltipTextKey) || `key: ${tooltipTextKey}`}
            </span>
          </div>
        </TooltipPortal>
      ) : null}
    </>
  );
};

export default Tooltip;
